import React, { useMemo, useRef, useState } from "react";
import "./ChartBlock.scss";
import { xlsxExport } from "../../../utils/xlsxExport";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  LabelList,
  ReferenceLine
} from "recharts";

const DEFAULT_COLORS = [
  "#E35D5D",
  "#31C48D",
  "#AC94FA",
  "#16BDCA",
  "#76A9FA",
  "#F17EB8",
  "#E3754D",
  "#8DA2FB",
  "#FCC80A"
];

const DARK_THEME = {
  grid: "#5B5C5E",
  xAxis: "#717173",
  yAxis: "#9B9C9E"
};

const BAR_HEIGHT = 16;
const BAR_GAP = 39;

const decodeHtml = (str) => {
  if (typeof str !== "string") return str;
  const doc = new DOMParser().parseFromString(str, "text/html");
  return doc.body.textContent || str;
};

const getChartHeight = (chartType, dataLength) => {
  switch (chartType) {
    case "pie":
      return 420;
    case "line":
      return 340;
    case "bar":
      return dataLength * (BAR_HEIGHT + BAR_GAP);
    default:
      return 260;
  }
};

const detectChartType = (chart) => {
  const { labels = [], datasets = [] } = chart;

  if (datasets.length > 1) return "line";

  if (chart.type === "funnel" || chart.type === "bar") return chart.type;

  if (datasets.length === 1 && labels.length <= 8) {
    const hasTimeSeries = labels.some((l) =>
      /^\d{4}[-/]|^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i.test(l)
    );
    if (!hasTimeSeries) return "pie";
  }

  return labels.length <= 6 ? "bar" : "line";
};

const transformData = (labels, datasets) =>
  labels.map((label, i) => {
    const point = { name: label };
    datasets.forEach((ds) => {
      const val = ds.values[i];
      point[ds.name] = val === null || val === undefined ? null : val;
    });
    return point;
  });

const formatNumber = (value) => {
  if (typeof value !== "number") return value;
  return value >= 1000
    ? value.toLocaleString("en-US", { maximumFractionDigits: 0 })
    : value.toLocaleString("en-US", { maximumFractionDigits: 2 });
};

const formatCurrency = (value) => `$${formatNumber(value)}`;

const formatPercent = (value) => `${formatNumber(value)}%`;

const getFormatter = (format) => {
  if (format === "currency") return formatCurrency;
  if (format === "percent") return formatPercent;
  return formatNumber;
};

const CustomTooltip = ({ active, payload, label, datasets, valueOnly, areaChart }) => {
  if (!active || !payload?.length) return null;

  const datasetByName = {};
  const formatMap = {};
  if (datasets) {
    datasets.forEach((ds) => {
      formatMap[ds.name] = ds.format;
      datasetByName[ds.name] = ds;
    });
  }

  if (valueOnly) {
    const fmt = getFormatter(formatMap[datasets?.[0]?.name]);
    return <div className="chart-tooltip">{fmt(payload[0].value)}</div>;
  }

  const isPredEntry = (entry) => {
    const ds = datasetByName[entry.name];
    return (ds && isPredictionDataset(ds)) || PREDICTION_DATASET_PATTERN.test(entry.name);
  };

  // Drop prediction entries at the bridge point to avoid showing duplicate values
  // e.g. both "Total Members: 483" and "Forecast: 483" on the same date.
  const entries = payload.filter((entry) => {
    if (!areaChart || !isPredEntry(entry)) return true;
    const duplicatesHistorical = payload.some(
      (other) => !isPredEntry(other) && other.value === entry.value
    );
    return !duplicatesHistorical;
  });

  return (
    <div className="chart-tooltip">
      <div className="chart-tooltip__label">{decodeHtml(label)}</div>
      {entries.map((entry, i) => {
        const fmt = getFormatter(formatMap[entry.name]);
        return (
          <div key={i} className="chart-tooltip__entry">
            <span style={{ color: entry.color }}>{decodeHtml(entry.name)}</span>: {fmt(entry.value)}
          </div>
        );
      })}
    </div>
  );
};

const getDatasetColor = (dataset, index) =>
  dataset.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length];

const PREDICTION_DATASET_PATTERN = /forecast|predicted|projection/i;
const PREDICTION_LABEL_PATTERN = /\(pred\)|\(forecast\)|\(projected\)|\*$/i;

const isPredictionDataset = (ds) =>
  ds.style === "dotted" || PREDICTION_DATASET_PATTERN.test(ds.name);

const splitPredictionDatasets = (labels, datasets) => {
  const predStartIdx = labels.findIndex((l) => PREDICTION_LABEL_PATTERN.test(l));
  if (predStartIdx === -1) return datasets;

  const split = [];
  datasets.forEach((ds) => {
    if (isPredictionDataset(ds)) {
      split.push(ds);
      return;
    }

    const actualValues = labels.map((_, i) => (i <= predStartIdx ? ds.values[i] : null));
    split.push({ ...ds, values: actualValues });

    const forecastValues = labels.map((_, i) => (i >= predStartIdx ? ds.values[i] : null));
    split.push({
      ...ds,
      name: `${ds.name} (Forecast)`,
      values: forecastValues,
      style: "dotted"
    });
  });
  return split;
};

const exportSvgToPng = (container, fileName) => {
  const svgEl = container.querySelector("svg");
  if (!svgEl) return;

  const svgData = new XMLSerializer().serializeToString(svgEl);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  const img = new Image();
  const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
  const url = URL.createObjectURL(svgBlob);

  img.onload = () => {
    canvas.width = img.width;
    canvas.height = img.height;
    ctx.fillStyle = "transparent";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0);
    URL.revokeObjectURL(url);
    const link = document.createElement("a");
    link.download = `${fileName}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };
  img.src = url;
};

const ChartTable = ({ chart }) => {
  const { labels = [], datasets = [] } = chart;
  return (
    <table className="chart-block__table">
      <thead>
        <tr>
          <th>Period</th>
          {datasets.map((ds) => (
            <th key={ds.name}>{ds.name}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {labels.map((label, i) => (
          <tr key={label}>
            <td>{label}</td>
            {datasets.map((ds) => {
              const fmt = getFormatter(ds.format);
              return <td key={ds.name}>{ds.values[i] != null ? fmt(ds.values[i]) : "—"}</td>;
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const HorizontalFunnel = ({ data, formatter, maxVal }) => {
  const [hovered, setHovered] = React.useState(null);
  const barHeight = 44;
  const gap = 8;
  const minWidthPct = 12;

  return (
    <div style={{ position: "relative" }}>
      {data.map((item, i) => {
        const widthPct = Math.max((item.value / maxVal) * 100, minWidthPct);
        const isHovered = hovered === i;
        return (
          <div
            key={i}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: i < data.length - 1 ? gap : 0
            }}
            onMouseEnter={() => setHovered(i)}
            onMouseLeave={() => setHovered(null)}
          >
            <div
              style={{
                width: `${widthPct}%`,
                height: barHeight,
                backgroundColor: item.fill,
                borderRadius: 10,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                opacity: isHovered ? 0.85 : 1,
                transition: "width 0.4s ease, opacity 0.15s"
              }}
            >
              <span style={{ color: "#fff", fontSize: 13, fontWeight: 700 }}>
                {decodeHtml(item.name)}: {formatter(item.value)}
              </span>
            </div>
          </div>
        );
      })}
      {hovered !== null && (
        <div
          className="chart-tooltip"
          style={{
            position: "absolute",
            top: hovered * (barHeight + gap) - 4,
            left: "50%",
            transform: "translate(-50%, -100%)",
            pointerEvents: "none",
            whiteSpace: "nowrap",
            zIndex: 10
          }}
        >
          {decodeHtml(data[hovered].name)}: {formatter(data[hovered].value)}
        </div>
      )}
    </div>
  );
};

export const ChartBlock = ({
  chart,
  chartId,
  enableTableView = false,
  enableExportPng = false,
  enableExportExcel = false,
  fileName
}) => {
  const chartRef = useRef(null);
  const [isTableView, setIsTableView] = useState(false);

  const { title, labels = [], datasets: rawDatasets = [] } = chart;
  const exportFileName = fileName || title || "chart";

  const chartType = useMemo(() => detectChartType(chart), [chart]);
  const datasets = useMemo(
    () => splitPredictionDatasets(labels, rawDatasets),
    [labels, rawDatasets]
  );
  const data = useMemo(() => transformData(labels, datasets), [labels, datasets]);
  const yAxisFormatter = useMemo(() => {
    const firstFormat = datasets[0]?.format;
    return getFormatter(firstFormat);
  }, [datasets]);

  const rotated = labels.length >= 12;

  const xAxisProps = useMemo(() => {
    const base = { dataKey: "name", tick: { fill: DARK_THEME.xAxis, fontSize: 12, height: 50 } };
    if (rotated) {
      return { ...base, interval: 0, angle: -35, textAnchor: "end", height: 90 };
    }
    return base;
  }, [labels, rotated]);

  if (!labels.length || !datasets.length) return null;

  const handleExportExcel = () => {
    const headers = ["Period", ...rawDatasets.map((ds) => ds.name)];
    const excelData = labels.map((label, i) => [
      label,
      ...rawDatasets.map((ds) => ds.values[i] ?? "")
    ]);
    xlsxExport({ data: excelData, headers, fileName: exportFileName, dataStructure: "aoa" });
  };

  const showAddons = enableTableView || enableExportPng || enableExportExcel;

  const renderChart = () => {
    switch (chartType) {
      case "pie":
        return (
          <PieChart>
            <Pie
              data={data.map((d) => ({ name: d.name, value: d[datasets[0].name] }))}
              cx="50%"
              cy="50%"
              outerRadius={160}
              dataKey="value"
              label={({ name, percent }) => `${decodeHtml(name)} (${(percent * 100).toFixed(0)}%)`}
              labelLine={false}
              minAngle={5}
            >
              {data.map((_, i) => (
                <Cell key={i} fill={DEFAULT_COLORS[i % DEFAULT_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip datasets={datasets} valueOnly />} />
          </PieChart>
        );

      case "funnel": {
        const funnelData = data
          .map((d, i) => ({
            name: d.name,
            value: d[datasets[0].name],
            fill: DEFAULT_COLORS[i % DEFAULT_COLORS.length]
          }))
          .filter((d) => d.value != null);
        const fmt = getFormatter(datasets[0]?.format);
        const maxVal = Math.max(...funnelData.map((d) => d.value));

        return (
          <HorizontalFunnel data={funnelData} formatter={fmt} maxVal={maxVal} datasets={datasets} />
        );
      }

      case "bar": {
        const hasNegative = data.some((d) =>
          datasets.some((ds) => typeof d[ds.name] === "number" && d[ds.name] < 0)
        );
        const maxAbs = Math.max(
          1,
          ...data.flatMap((d) =>
            datasets.map((ds) => Math.abs(typeof d[ds.name] === "number" ? d[ds.name] : 0))
          )
        );
        return (
          <BarChart
            data={data}
            layout="vertical"
            margin={{ right: hasNegative ? 116 : 60 }}
            overflow="visible"
          >
            <XAxis
              type="number"
              hide
              domain={hasNegative ? [-maxAbs * 1.2, maxAbs * 1.2] : undefined}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              type="category"
              dataKey="name"
              tick={(props) => {
                const { x, y, payload } = props;
                const labelWidth = x - 16;
                const labelHeight = 42;
                return (
                  <foreignObject
                    x={0}
                    y={y - labelHeight / 2}
                    width={labelWidth}
                    height={labelHeight}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        height: labelHeight
                      }}
                    >
                      <div
                        style={{
                          color: DARK_THEME.yAxis,
                          fontSize: 14,
                          lineHeight: "21px",
                          textAlign: "right",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          wordWrap: "break-word",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical"
                        }}
                      >
                        {decodeHtml(payload.value)}
                      </div>
                    </div>
                  </foreignObject>
                );
              }}
              width={160}
            />
            <Tooltip content={<CustomTooltip datasets={datasets} />} cursor={false} />
            {hasNegative && <ReferenceLine x={0} stroke={DARK_THEME.grid} />}
            {datasets.length > 1 && (
              <Legend wrapperStyle={{ color: DARK_THEME.yAxis }} formatter={decodeHtml} />
            )}
            {datasets.map((ds) => (
              <Bar
                barSize={BAR_HEIGHT}
                key={ds.name}
                dataKey={ds.name}
                fill="#E35D5D"
                radius={[0, 8, 8, 0]}
              >
                <LabelList
                  dataKey={ds.name}
                  content={(props) => {
                    const { x, y, width, height, value } = props;
                    if (value === null || value === undefined) return null;
                    const isNegative = value < 0;
                    const leftEdge = Math.min(x, x + width);
                    const rightEdge = Math.max(x, x + width);
                    const labelX = isNegative ? leftEdge - 12 : rightEdge + 12;
                    return (
                      <text
                        x={labelX}
                        y={y + height / 2}
                        fill="#9B9C9E"
                        fontSize={14}
                        textAnchor={isNegative ? "end" : "start"}
                        dominantBaseline="central"
                      >
                        {yAxisFormatter(Math.abs(value))}
                      </text>
                    );
                  }}
                />
              </Bar>
            ))}
          </BarChart>
        );
      }

      default:
        return (
          <AreaChart data={data} margin={{ right: 40, left: 40 }}>
            <defs>
              {datasets.map((ds, i) => {
                const color = getDatasetColor(ds, i);
                return (
                  <linearGradient
                    key={`gradient-${i}`}
                    id={`areaGradient-${chartId}-${i}`}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="0%" stopColor={color} stopOpacity={0.4} />
                    <stop offset="100%" stopColor={color} stopOpacity={0.05} />
                  </linearGradient>
                );
              })}
            </defs>
            <CartesianGrid
              strokeDasharray="4 4"
              stroke={DARK_THEME.grid}
              horizontal
              vertical={false}
            />
            <XAxis
              {...xAxisProps}
              axisLine={false}
              tickLine={false}
              dy={10}
              tickFormatter={decodeHtml}
            />
            <YAxis
              tick={{ fill: DARK_THEME.yAxis, fontSize: 12 }}
              tickFormatter={yAxisFormatter}
              axisLine={false}
              tickLine={false}
              dx={-20}
              tickCount={4}
            />
            <Tooltip content={<CustomTooltip datasets={datasets} areaChart />} />
            {datasets.length > 1 && (
              <Legend
                wrapperStyle={{ color: DARK_THEME.yAxis, ...(rotated ? { paddingTop: 30 } : {}) }}
                formatter={decodeHtml}
              />
            )}
            {datasets.map((ds, i) => {
              const color = getDatasetColor(ds, i);
              const prediction = isPredictionDataset(ds);
              return (
                <Area
                  key={ds.name}
                  type="linear"
                  dataKey={ds.name}
                  stroke={color}
                  strokeWidth={3.5}
                  strokeDasharray={prediction ? "6 3" : undefined}
                  fill={`url(#areaGradient-${chartId}-${i})`}
                  dot={{ r: 3, fill: color }}
                />
              );
            })}
          </AreaChart>
        );
    }
  };

  return (
    <div className="chart-block">
      {title && <div className="chart-block__title" dangerouslySetInnerHTML={{ __html: title }} />}
      {showAddons && (
        <div className="relative-50">
          <div className="chart-addons-container">
            {enableExportPng && !isTableView && (
              <div className="chart-addons-control-item">
                <a
                  title="Export to PNG"
                  className="export_to_png"
                  style={{ cursor: "pointer" }}
                  onClick={() => exportSvgToPng(chartRef.current, exportFileName)}
                >
                  <span className="glyphicon glyphicon-picture" />
                </a>
              </div>
            )}
            {enableExportExcel && (
              <div className="chart-addons-control-item" style={{ cursor: "pointer" }}>
                <a
                  title="Export to Excel"
                  className="export_to_csv"
                  style={{ color: "#8fc447" }}
                  onClick={handleExportExcel}
                >
                  <span className="glyphicon glyphicon-download-alt" />
                </a>
              </div>
            )}
            {enableTableView && (
              <div className="chart-addons-control-item">
                <a
                  className="switch_chart_type"
                  title={`Switch to ${isTableView ? "Chart" : "Table"} View`}
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsTableView((v) => !v);
                  }}
                >
                  <span
                    className={`glyphicon glyphicon-${isTableView ? "equalizer" : "th-list"}`}
                  />
                </a>
              </div>
            )}
          </div>
        </div>
      )}
      <div className="chart-block__container" ref={chartRef}>
        {isTableView ? (
          <ChartTable chart={chart} />
        ) : (
          <ResponsiveContainer width="100%" height={getChartHeight(chartType, data.length)}>
            {renderChart()}
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default ChartBlock;
